import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import ScrollToPlugin from 'gsap/ScrollToPlugin'
import Player from '@vimeo/player'
import { module as BaseModule } from 'modujs'

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(ScrollToPlugin)

const debugEvents = {
  //eslint-disable-line
  // markers: true,
  onEnter: () => console.log('enter'), //eslint-disable-line
  onEnterBack: () => console.log('enter back'), //eslint-disable-line
  onLeave: () => console.log('leave'), //eslint-disable-line
  onLeaveBack: () => console.log('leave back') //eslint-disable-line
}

export default class extends BaseModule {
  init () {
    this.player()
    this.intro()
    this.dark()
    this.light()
    this.projects()
    this.numbers()
    this.anchors()
  }

  dark () {
    const index = document.querySelector('.c-index')
    const indexLink = document.querySelector(
      '.c-header__menu li:nth-child(1) a'
    )

    ScrollTrigger.create({
      trigger: index,
      id: 'index',
      start: 'top top',
      start: 'top-=20 top',
      onEnter: () => {
        document.body.classList.remove('has-light-theme')
        indexLink.innerHTML = 'X'
        indexLink.classList.add('disabled')
        document.body.classList.add('s-index')
      },
      onLeaveBack: () => {
        document.body.classList.add('has-light-theme')
        indexLink.innerHTML = 'Index'
        indexLink.classList.remove('disabled')
        document.body.classList.remove('s-index')
      }
    })
  }

  anchors () {
    const anchors = document.querySelectorAll('.c-frontpage__anchor')
    const gotos = document.querySelectorAll('.js-goto-project')
    gotos.forEach((el, i) => {
      const { id } = el.dataset
      el.addEventListener('click', () => {
        gsap.to(window, {
          duration: 1,
          scrollTo: `#${id}`
        })
      })
    })

    const scrollNexts = document.querySelectorAll('.c-frontpage__scroll')
    scrollNexts.forEach((scrollNext, index) => {
      scrollNext.addEventListener('click', () => {
        if (anchors[index]) {
          gsap.to(window, { scrollTo: anchors[index] })
        } else {
          gsap.to(window, { scrollTo: '.c-index' })
        }
      })
    })
  }

  numbers () {
    const index = document.querySelector('.c-frontpage__index')
    index.addEventListener('mouseenter', () => {
      const tl = gsap.timeline()
      tl.to('.c-frontpage__current', { duration: 0.25, autoAlpha: 0 })
      tl.set('.c-frontpage__current button', {
        autoAlpha: 1,
        display: 'inline-block'
      })
      tl.to('.c-frontpage__current', { duration: 0.25, autoAlpha: 1 })
    })

    index.addEventListener('mouseleave', () => {
      const tl = gsap.timeline()
      tl.to('.c-frontpage__current', { duration: 0.25, autoAlpha: 0 })
      tl.set('.c-frontpage__current button:not(.active)', {
        autoAlpha: 0,
        display: 'none'
      })
      tl.set('.c-frontpage__current button.active', {
        autoAlpha: 1,
        display: 'inline-block'
      })
      tl.to('.c-frontpage__current', { duration: 0.25, autoAlpha: 1 })
    })
  }

  player () {
    if (window.innerWidth < 900) return

    let state = false
    const iframe = document.querySelector('#showreel')
    const soundBtn = document.querySelector('.c-frontpage__sound')
    const playBtn = document.querySelector('.c-frontpage__play')

    const player = new Player(iframe)

    const play = async () => {
      try {
        if (state) return
        await player.play()
        playBtn.innerHTML = 'Pause'
        state = true
      } catch (error) {
        throw new Error(`Playing showreel - ${error}`)
      }
    }

    const pause = async () => {
      try {
        if (!state) return
        await player.pause()
        playBtn.innerHTML = 'Play'
        state = false
      } catch (error) {
        throw new Error(`Pausing showreel - ${error}`)
      }
    }

    const togglePlay = async () => {
      await player
      if (state === true) pause()
      if (state === false) play()
    }

    playBtn.addEventListener('click', async () => {
      if (window.innerWidth < 900) return
      try {
        togglePlay()
      } catch (error) {
        throw new Error(`Oh no - ${error}`)
      }
    })

    soundBtn.addEventListener('click', async () => {
      try {
        const muted = await player.getMuted()
        await player.setVolume(1)

        if (muted) {
          player.setMuted(false)
          soundBtn.innerHTML = 'Sound off'
          player.setVolume(1)
        } else {
          player.setMuted(true)
          soundBtn.innerHTML = 'Sound on'
        }
      } catch (error) {
        throw new Error(error)
      }
    })

    ScrollTrigger.create({
      trigger: iframe,
      start: 'start',
      end: 'bottom top',
      onEnter: async () => {
        if (window.innerWidth < 900) return
        play()
      },
      onLeave: async () => {
        if (window.innerWidth < 900) return
        await player
        pause()
      },
      onEnterBack: async () => {
        if (window.innerWidth < 900) return
        play()
      }
    })
  }

  intro () {
    const trigger = document.querySelector('.c-frontpage__trigger')
    const items = document.querySelectorAll('.c-frontpage__intro__item')
    const headline = document.querySelector('.c-frontpage__intro__headline')
    const player = document.querySelector(
      '.c-frontpage__intro__showreel .c-frontpage__project__ui'
    )
    const ui = document.querySelectorAll(
      '.c-header__logo, .c-header__menu, .c-header__leap, .c-header__burger, .c-frontpage__infos'
    )

    gsap.set(headline, { autoAlpha: 0, yPercent: 50 })
    gsap.set(ui, { autoAlpha: 0 })

    // Part 1 : Intro Elements
    gsap.to(items, {
      scrollTrigger: {
        id: 'intro-items',
        trigger: '.c-frontpage__trigger',
        start: 'top top',
        end: () => `top+=${trigger.offsetHeight * 0.75} top`,
        toggleActions: 'play none play reverse'
      },
      top: () => 0,
      ease: 'power1.inOut',
      yPercent: () => 0,
      fontSize: () =>
        getComputedStyle(document.documentElement).getPropertyValue('--f-lg')
    })

    const mm = gsap.matchMedia()

    mm.add('(min-width: 900px)', () => {
      // Part 2 : Headline
      gsap.to(headline, {
        scrollTrigger: {
          id: 'intro-headline',
          trigger: '.c-frontpage__trigger',
          start: () => `top+=${trigger.offsetHeight * 0.33} top`,
          end: () => 'bottom bottom',
          toggleActions: 'play reverse play reverse'
          // markers: true,
        },
        autoAlpha: 1,
        yPercent: -50,
        ease: 'power2.out',
        duration: 0.35
      })

      // Part 3 : Hide Intro Elements
      gsap.to(items, {
        scrollTrigger: {
          id: 'intro-items-out',
          trigger: '.c-frontpage__intro__showreel',
          start: () => 'top-=10% top',
          toggleActions: 'play none play reverse'
        },
        autoAlpha: 0,
        duration: 0.35
      })

      // Part 4 : Show header
      gsap.to(ui, {
        scrollTrigger: {
          id: 'intro-ui-in',
          trigger: '.c-frontpage__intro__showreel',
          start: () => 'top-=5% top',
          toggleActions: 'play none play reverse'
        },
        autoAlpha: 1,
        duration: 0.5
      })

      // Part 5 : Hide player
      gsap.to(player, {
        scrollTrigger: {
          id: 'intro-player-out',
          trigger: '.c-frontpage__intro__showreel',
          start: () => 'top+=100% top',
          end: () => 'bottom bottom',
          toggleActions: 'play none play reverse'
        },
        opacity: 0,
        duration: 0.35
      })

      return () => {}
    })

    mm.add('(max-width: 899px)', () => {
      gsap.to(items, {
        scrollTrigger: {
          id: 'intro-mobile-items-out',
          trigger: '.c-index',
          start: () => 'top-=5% top',
          toggleActions: 'play none play reverse'
        },
        autoAlpha: 0,
        duration: 0.35
      })
      gsap.to(ui, {
        scrollTrigger: {
          id: 'intro-mobile-ui-in',
          trigger: '.c-index',
          start: () => 'top-=2% top',
          toggleActions: 'play none play reverse'
        },
        autoAlpha: 1,
        duration: 0.35
      })

      return () => {}
    })
  }

  light () {
    const logo = document.querySelector('.c-header__logo')
    const mm = gsap.matchMedia()

    mm.add('(min-width: 900px)', () => {
      ScrollTrigger.create({
        trigger: '.c-frontpage__trigger',
        start: 'top top',
        end: `bottom top+=${logo.offsetHeight * 0.5}`,
        onLeave: () => {
          document.body.classList.add('has-light-theme')
        },
        onEnterBack: () => {
          document.body.classList.remove('has-light-theme')
        }
      })
      return () => {}
    })
  }

  projects () {
    const projects = document.querySelectorAll('.c-frontpage__project')
    const headlines = document.querySelectorAll(
      '.c-frontpage__project .c-frontpage__headline'
    )
    const UIs = document.querySelectorAll(
      '.c-frontpage__project .c-frontpage__project__ui'
    )
    const index = document.querySelectorAll('.c-frontpage__index')
    const projectLinks = document.querySelectorAll('.c-frontpage__index button')

    const mm = gsap.matchMedia()
    mm.add('(min-width: 900px)', () => {
      gsap.set(UIs, { autoAlpha: 0 })
      gsap.to(index, {
        scrollTrigger: {
          id: 'index-projects',
          trigger: '.c-frontpage__projects',
          start: 'top top',
          end: () => 'bottom bottom',
          toggleActions: 'play reverse play reverse'
          // markers: true
        },
        autoAlpha: 1
      })

      projects.forEach((project, i) => {
        const headline = headlines[i]
        const ui = UIs[i]
        const link = projectLinks[i]

        ScrollTrigger.create({
          trigger: project,
          start: () => 'top 40%',
          end: () => 'bottom 60%',
          // markers: true,
          onEnter: () => {
            console.log('enter', i)
            gsap.to([ui, headline], {
              autoAlpha: 1,
              ease: 'power2.out',
              duration: 0.35
            })
          },
          onEnterBack: () => {
            console.log('enter ack', i)
            gsap.to([ui, headline], {
              autoAlpha: 1,
              ease: 'power2.out',
              duration: 0.35
            })
          },
          onLeave: () => {
            console.log('leave', i)
            gsap.to([ui, headline], {
              autoAlpha: 0,
              ease: 'power2.out',
              duration: 0.35
            })
          },
          onLeaveBack: () => {
            console.log('leave back', i)
            gsap.to([ui, headline], {
              autoAlpha: 0,
              ease: 'power2.out',
              duration: 0.35
            })
          }
        })

        ScrollTrigger.create({
          trigger: project,
          id: 'index',
          start: () => 'top top',
          end: () => 'bottom bottom',
          onEnter: () => {
            projectLinks.forEach(el => el.classList.remove('active'))
            link.classList.add('active')
          },
          onEnterBack: () => {
            projectLinks.forEach(el => el.classList.remove('active'))
            link.classList.add('active')
          }
        })
        return () => {}
      })
    })
  }
}
